import React, { useState, useEffect } from 'react';
import {
  PageHeader, SpecStrip, Stat, List, Row, EmptyState, Callout, Button, TYPE, cn,
} from './ui';
import { adminApi, AdminUser } from '../services/api';

const Admin: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <>
        <PageHeader heading="Admin" />
        <p className={cn(TYPE.meta, 'text-ink-muted mt-11')}>Loading users…</p>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader heading="Admin" />
        <Callout className="mt-11">
          <div className={cn(TYPE.metaTight, 'text-bad')}>Could not load users</div>
          <p className={cn(TYPE.body, 'text-ink-soft mt-2')}>{error}</p>
          <div className="mt-5">
            <Button variant="danger" onClick={loadUsers}>Retry</Button>
          </div>
        </Callout>
      </>
    );
  }

  const totals = users.reduce(
    (acc, u) => ({
      bills: acc.bills + u._count.bills,
      incomes: acc.incomes + u._count.incomes,
      creditCards: acc.creditCards + u._count.creditCards,
      loans: acc.loans + u._count.loans,
    }),
    { bills: 0, incomes: 0, creditCards: 0, loans: 0 }
  );

  return (
    <>
      <PageHeader heading="User Management" subhead="Every account and what it holds." />

      <SpecStrip className="mt-11" facts={[<>{users.length} users</>]}>
        <Stat value={users.length} label="Users" />
        <Stat value={totals.bills} label="Expenses" />
        <Stat value={totals.incomes} label="Incomes" />
        <Stat value={totals.creditCards} label="Cards" />
        <Stat value={totals.loans} label="Loans" />
      </SpecStrip>

      <div className="mt-11">
        {users.length === 0 ? (
          <EmptyState>No users found</EmptyState>
        ) : (
          <List>
            {/* Wrap-safe rows rather than a scrolling table, so every column
                survives down to 375px. */}
            {users.map((user, i) => (
              <Row key={user.id}>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 py-4">
                  <span className={cn(TYPE.index, 'text-ink-muted w-8 shrink-0')}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-[2_1_220px] min-w-0">
                    <div className={cn(TYPE.rowTitle, 'text-ink truncate')}>{user.email}</div>
                    <div className={cn(TYPE.metaTight, 'text-ink-muted mt-1.5')}>
                      {user.name || 'No name'}
                      <span className="text-ink-muted"> · </span>Joined {formatDate(user.createdAt)}
                      <span className="text-ink-muted"> · </span>Updated {formatDate(user.updatedAt)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-7 gap-y-2 shrink-0">
                    {([
                      ['Expenses', user._count.bills],
                      ['Incomes', user._count.incomes],
                      ['Cards', user._count.creditCards],
                      ['Loans', user._count.loans],
                    ] as const).map(([label, value]) => (
                      <div key={label} className="min-w-[62px]">
                        <div className={cn(TYPE.rowFigure, "text-ink")}>
                          {value}
                        </div>
                        <div className={cn(TYPE.metaTight, 'text-ink-muted mt-1')}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Row>
            ))}
          </List>
        )}
      </div>
    </>
  );
};

export default Admin;
